// custom-size-line.ts — the extra as a LINE: creation, uniqueness, life cycle (§4).
//
// The extra is a line linked to a parent PHO stand line, and it never exists alone.
//
// It is a child line IN THE SENSE OF §12, so it reuses what already exists (`linkId` + `linkLabel`,
// `idsWithLinked`, `removeLines`, `duplicateLines`) by adding one more label —
// `LINK_LABELS.customSizeChild` — rather than duplicating the cascade machinery.
//
// The single adaptation needed lives in `triggers.ts`: §7 SYMMETRY erases children whose trigger
// component vanished from the parent, so an extra (born from a human decision, not a component)
// had to be declared never obsolete.

import { type QuoteLine, num, findStand } from "../core/core";
import { LINK_LABELS } from "./triggers";
import { customSizeRateForStand } from "./stands";
import type { CustomSizeRate } from "../../models/data.model";

/**
 * Is the line a custom-size extra? The marker is the link label, as for the §7 children.
 * @param line - Line to test.
 * @returns True when the line is a custom-size extra.
 */
export function isCustomSizeSurcharge(line: Pick<QuoteLine, "linkLabel">): boolean {
  // Legacy French prefix kept so old drafts still identify the custom-size child (same as triggers.ts).
  const legacy = "Supplément sur-mesure de la ligne ";
  const label = line.linkLabel ?? "";
  return label === LINK_LABELS.customSizeChild || label === legacy || label.startsWith(legacy);
}

/**
 * Can the parent take an extra? Two conditions, neither of which is a line type: a rate must
 * exist for its (family, component, faces), and it must not already carry one.
 * @param parent - Candidate parent line.
 * @param lines - All quote lines.
 * @returns True when the parent can take an extra.
 */
export function canReceiveExtra(parent: QuoteLine, lines: readonly QuoteLine[]): boolean {
  return parentRate(parent) != null && extraOf(parent, lines) == null;
}

/**
 * Rate applicable to a parent, derived from its catalogue reference. `null` = custom size not open.
 * @param parent - Parent line.
 * @returns The applicable rate, or `null`.
 */
export function parentRate(parent: QuoteLine): CustomSizeRate | null {
  const ref = String(parent.ref ?? "");
  const stand = ref ? findStand(ref) : undefined;
  return stand ? customSizeRateForStand(stand) : null;
}

/**
 * The extra ALREADY carried by this parent, or `null`.
 *
 * A parent carries at most ONE extra, after which the button switches to edit mode. This
 * function IS that rule, so uniqueness is not decided in two places.
 * @param parent - Parent line.
 * @param lines - All quote lines.
 * @returns The extra line, or `null`.
 */
export function extraOf(parent: QuoteLine, lines: readonly QuoteLine[]): QuoteLine | null {
  if (parent.id == null) return null;
  return lines.find((line) => isCustomSizeSurcharge(line) && line.linkId === parent.id) ?? null;
}

/**
 * NOMINAL dimensions of a parent, read from the catalogue — never from the format entered on the line.
 * @param parent - Parent line.
 * @returns The nominal dimensions, or `null` when the reference is unknown.
 */
export function baseDimensions(parent: QuoteLine): { L: number; H: number } | null {
  const ref = String(parent.ref ?? "");
  const stand = ref ? findStand(ref) : undefined;
  return stand && stand.L > 0 && stand.H > 0 ? { L: stand.L, H: stand.H } : null;
}

/**
 * TIER 1 sale price of the parent, read from the CATALOGUE. `null` when the reference is unknown.
 *
 * NOT from the line result: a STAND line carries no `pvPublic` (that field is in €/m², meaningless
 * for a product sold per piece), so reading it returned ZERO and the catalogue floor then compared
 * the extra ALONE against a complete product price, refusing legitimate formats.
 * @param parent - Parent line.
 * @returns The tier 1 sale price, or `null`.
 */
export function parentTier1Pv(parent: QuoteLine): number | null {
  const ref = String(parent.ref ?? "");
  const stand = ref ? findStand(ref) : undefined;
  return stand ? num(stand.pv_tier1) : null;
}

export interface ExtraCreation {
  /** The child line, to insert RIGHT AFTER its parent. */
  child: QuoteLine;
  /** The parent, updated with its reciprocal link. */
  parent: QuoteLine;
}

/**
 * Creates the {linked parent, extra child} pair. The caller inserts the child right after the
 * parent and refreshes the cross-references (`applyLinkRefs`), exactly as for a §7 child.
 *
 * The child carries ONLY the final format: no reference (derived), no quantity (the parent's) and no
 * line dimensions — it is not a product but a price top-up.
 * @param parent - Parent stand line.
 * @param final - Requested final dimensions.
 * @param childId - Id to give the child line.
 * @returns The child line and the updated parent.
 */
export function createExtra(parent: QuoteLine, final: { L: number; H: number }, childId: number): ExtraCreation {
  const child: QuoteLine = {
    type: parent.type,
    id: childId,
    linkId: parent.id ?? null,
    linkLabel: LINK_LABELS.customSizeChild,
    customSizeFinal: { L: num(final.L), H: num(final.H) },
  };
  // The parent takes the PARENT label, never the child one: otherwise the §7 cascade would mistake it
  // for an auto-created child and delete it along with its own child.
  return { child, parent: { ...parent, linkId: childId, linkLabel: LINK_LABELS.customSizeParent } };
}

/**
 * Changes the format of an existing extra. Nothing else is editable on this line (§4).
 * @param child - Existing extra line.
 * @param final - New requested dimensions.
 * @returns The updated extra line.
 */
export function updateExtraFormat(child: QuoteLine, final: { L: number; H: number }): QuoteLine {
  return { ...child, customSizeFinal: { L: num(final.L), H: num(final.H) } };
}
